import React, { useEffect } from 'react';
import { TimePickerComponent, toTimePickerDayjs, RoundToggle } from '@umanforge/common';

import InfoTooltip from 'components/infoTooltip.component';

import { ColorPickerInput } from '@common_components/inputs/color_picker/colorPickerInput.component';
import SelectInput from '@common_components/inputs/selects/selectInput.component';
import InputText from '@common_components/inputs/textInput.component';
import type { TargetSectionProps } from './chartSectionList.interfaces';

const TargetSection: React.FC<TargetSectionProps> = ( {
  t: translate,
  newSetting,
  hasTargetValue,
  onChangeField,
  isEditingExisting = false,
  fetchTargetTypes,
  fetchAlertThemes,
  themesRefreshTrigger,
} ) => {

  const resetTargetSection = () => {
    onChangeField( 'hasTarget', '0' );
    onChangeField( 'targetName', '' );
    onChangeField( 'targetValue', '' );
    onChangeField( 'targetType', '' );
    onChangeField( 'targetColor', '' );
    onChangeField( 'createTicketOnTargetMiss', '0' );
    onChangeField( 'ticketCreationHour', '' );
    onChangeField( 'alertThemeId', '' );
  };

  useEffect( () => {
    if ( newSetting.type === 'progress_gauge' ) {
      resetTargetSection();
    } else if ( newSetting.type === 'auto_increment_gauge' ) {
      if ( !isEditingExisting ) {
        onChangeField( 'hasTarget', '0' );
        onChangeField( 'targetName', '' );
        onChangeField( 'targetValue', '' );
        onChangeField( 'targetType', '' );
        onChangeField( 'targetColor', '' );
        onChangeField( 'createTicketOnTargetMiss', '1' );
        onChangeField( 'ticketCreationHour', '' );
        onChangeField( 'alertThemeId', '' );
      }
    }
  }, [ newSetting.type ] );

  if ( newSetting.type === 'progress_gauge' || newSetting.type === 'auto_increment_gauge' ) {
    if ( newSetting.type === 'progress_gauge' ) {
      return (
        <div className="col-span-2 mt-1">
          <p className="text-[11px] font-semibold text-gray-700 mb-1">
            {translate( 'translation:target_section' )}
          </p>
          <hr className="border-gray-200 mb-2" />
          <p className="text-[11px] text-gray-400 italic">
            {translate( 'translation:no_target_for_progress_gauge' )}
          </p>
        </div>
      );
    }

    return (
      <div className="col-span-2 mt-1">
        <p className="text-[11px] font-semibold text-gray-700 mb-1">
          {translate( 'translation:target_section' )}
        </p>
        <hr className="border-gray-200 mb-2" />
        <div className="col-span-2 mt-1">
          <div className="col-span-2 mt-1">
            <p className="text-[11px] text-gray-500 mb-2">
              {translate( 'translation:auto_increment_gauge' )}
            </p>
            <div>
              <RoundToggle
                label={ translate( 'translation:create_alert_on_reset' ) }
                checked={ newSetting.createTicketOnTargetMiss === '1' }
                onChange={ ( isChecked: boolean ) => {
                  onChangeField( 'createTicketOnTargetMiss', isChecked ? '1' : '0' );
                  if ( !isChecked ) {
                    onChangeField( 'ticketCreationHour', '' );
                    onChangeField( 'alertThemeId', '' );
                  }
                } }
              />
              { newSetting.createTicketOnTargetMiss === '1' && (
                <div className="mt-3">
                  <SelectInput
                    className="w-full col-span-2"
                    label={ translate( 'translation:ticket_theme_label' ) }
                    labelPosition="top"
                    isRequired={ newSetting.createTicketOnTargetMiss === '1' }
                    fetchOptions={ fetchAlertThemes }
                    refreshTrigger={ themesRefreshTrigger }
                    selected={ newSetting.alertThemeId }
                    onSelect={ val => { return onChangeField( 'alertThemeId', val ); } }
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="col-span-2 mt-1">
        <p className="text-[11px] font-semibold text-gray-700 mb-1">
          {translate( 'translation:target_section' )}
        </p>
        <hr className="border-gray-200 mb-2" />
        <RoundToggle
          label={ translate( 'translation:has_target_section' ) }
          checked={ newSetting.hasTarget === '1' }
          onChange={ ( isChecked: boolean ) => {
            onChangeField( 'hasTarget', isChecked ? '1' : '0' );
            if ( !isChecked ) {
              resetTargetSection();
            }
          } }
        />
      </div>

      { newSetting.hasTarget === '1' && (
        <>
          <InputText
            className="w-full"
            label={translate( 'translation:target_name_placeholder' ) as string}
            labelPosition="top"
            isRequired={hasTargetValue}
            value={newSetting.targetName}
            onChange={val => { return onChangeField( 'targetName', val ); }}
          />
          <InputText
            className="w-full"
            label={translate( 'translation:target_value_placeholder' ) as string}
            labelPosition="top"
            type="number"
            min={0}
            isRequired={hasTargetValue}
            value={newSetting.targetValue}
            onChange={val => { return onChangeField( 'targetValue', val ); }}
          />
          <SelectInput
            className="w-full"
            label={translate( 'translation:target_type' ) as string}
            labelPosition="top"
            isRequired={hasTargetValue}
            fetchOptions={fetchTargetTypes}
            selected={newSetting.targetType}
            onSelect={val => { return onChangeField( 'targetType', val ); }}
          />
          { newSetting.type !== 'custom_gauge' && (
            <ColorPickerInput
              labelPosition="top"
              label={translate( 'translation:target_color_placeholder' ) as string}
              isRequired={hasTargetValue && !newSetting.targetColor}
              color={newSetting.targetColor}
              onChange={val => { return onChangeField( 'targetColor', val ); }}
            />
          ) }

          <div className="col-span-2 mt-1">
            <RoundToggle
              label={ translate( 'translation:create_ticket_on_target_miss' ) }
              checked={ newSetting.createTicketOnTargetMiss === '1' }
              onChange={ ( isChecked: boolean ) => {
                onChangeField( 'createTicketOnTargetMiss', isChecked ? '1' : '0' );
                if ( !isChecked ) {
                  onChangeField( 'ticketCreationHour', '' );
                  onChangeField( 'alertThemeId', '' );
                } else if ( isChecked ) {
                  onChangeField( 'ticketCreationHour', '00:00' );
                }
              } }
            />
          </div>

          { newSetting.createTicketOnTargetMiss === '1' && (
            <>
              <SelectInput
                className="w-full col-span-2"
                label={ translate( 'translation:ticket_theme_label' ) }
                labelPosition="top"
                isRequired={ newSetting.createTicketOnTargetMiss === '1' }
                fetchOptions={ fetchAlertThemes }
                refreshTrigger={ themesRefreshTrigger }
                selected={ newSetting.alertThemeId }
                onSelect={ val => { return onChangeField( 'alertThemeId', val ); } }
              />
              <div className="col-span-2 flex items-center gap-3">
                <span className="min-w-max text-[12px] font-medium text-gray-700">
                  { translate( 'translation:ticket_creation_hour_label' ) }
                </span>
                <TimePickerComponent
                  value={ toTimePickerDayjs( newSetting.ticketCreationHour ) }
                  step={ 15 }
                  onChange={ time => { onChangeField( 'ticketCreationHour', time.format( 'HH:mm' ) ); } }
                />
                <InfoTooltip
                  label="ticket_creation_hour"
                  tooltip={ translate( 'translation:ticket_creation_hour' ) }
                />
              </div>
            </>
          ) }
        </>
      ) }

    </>
  );
};

export default TargetSection;
